import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { ProactiveMemoryResolveResult } from "@/lib/memory/proactiveMemoryApi";
import { appQueryKeys } from "@/lib/queryKeys";

interface InvalidateTranscriptAnalysisOptions {
  queryClient: QueryClient;
  transcriptId?: string | null;
  leaderId?: string | null;
  coachId?: string | null;
  proactiveMemoryReadback?: ProactiveMemoryReadbackMode;
  extraQueryKeys?: ReadonlyArray<readonly unknown[]>;
}

export type ProactiveMemoryReadbackMode = "nonavailable" | "all" | "none";

async function refreshProactiveMemory(
  queryClient: QueryClient,
  queryKey: QueryKey,
  mode: Exclude<ProactiveMemoryReadbackMode, "none">,
) {
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });
  if (!query) return;

  const cachedResult = query.state.data as ProactiveMemoryResolveResult | undefined;
  if (mode === "nonavailable" && cachedResult?.status === "available") return;

  const wasFetching = query.state.fetchStatus === "fetching";
  if (!query.isActive() && !wasFetching) {
    if (mode === "all" || cachedResult?.status !== "available") {
      queryClient.removeQueries({ queryKey, exact: true, type: "inactive" });
    }
    return;
  }

  if (wasFetching) {
    await queryClient.refetchQueries(
      { queryKey, exact: true, type: "all" },
      { cancelRefetch: false },
    );

    const settledQuery = queryClient.getQueryCache().find({ queryKey, exact: true });
    const settledResult = settledQuery?.state.data as ProactiveMemoryResolveResult | undefined;
    if (!settledQuery) return;
    if (mode === "nonavailable" && settledResult?.status === "available") return;
    // A joined resolve may already have reserved the strongest candidate. Keep
    // that exact delivery instead of discarding its token and resolving again,
    // which can legitimately return silence for the now-reserved candidate.
    if (mode === "all" && settledResult?.status === "available") return;
    if (!settledQuery.isActive()) {
      queryClient.removeQueries({ queryKey, exact: true, type: "inactive" });
      return;
    }

    if (mode === "all") {
      queryClient.setQueryData<ProactiveMemoryResolveResult>(queryKey, { status: "unavailable" });
    }
    await queryClient.refetchQueries(
      { queryKey, exact: true, type: "active" },
      { cancelRefetch: false },
    );
  } else {
    if (mode === "all") {
      queryClient.setQueryData<ProactiveMemoryResolveResult>(queryKey, { status: "unavailable" });
    }
    await queryClient.refetchQueries(
      { queryKey, exact: true, type: "active" },
      { cancelRefetch: false },
    );
  }

  const queryAfterRefresh = queryClient.getQueryCache().find({ queryKey, exact: true });
  const resultAfterRefresh = queryAfterRefresh?.state.data as ProactiveMemoryResolveResult | undefined;
  if (
    queryAfterRefresh
    && !queryAfterRefresh.isActive()
    && resultAfterRefresh?.status !== "available"
  ) {
    queryClient.removeQueries({ queryKey, exact: true, type: "inactive" });
  }
}

function matchesTranscriptScope(key: readonly unknown[], scope: { coachId?: string | null; leaderId?: string | null; transcriptId?: string | null }): boolean {
  const { coachId, leaderId, transcriptId } = scope;
  const family = key[0];
  const leaderFamilies = ["weekly-moments", "today-schedule", "upcoming-meetings", "dashboard-data", "transcript-uploads-this-week",
    "leader-insights", "transcript-analyses", "leader-transcript-trends", "post-meeting-list", "today-analyzed-transcript-count",
    "leader-experience", "veera-selected-meeting-context", "veera-chat-context"];
  if (leaderFamilies.includes(String(family))) return Boolean(leaderId && key[1] === leaderId);
  if (["coach-new-evidence", "coach-review-count", "coach-session-records-needs-review"].includes(String(family))) return Boolean(coachId && key[1] === coachId);
  switch (family) {
    case "living-constellation-state": return Boolean((coachId ?? leaderId) && key[1] === (coachId ?? leaderId) && (!leaderId || key[2] === leaderId));
    case "coach-transcript-analyses": return Boolean(coachId && key[1] === coachId && (key.length === 2
      || (key[2] === "evidence" && (!leaderId || key[3] === leaderId) && (!transcriptId || key[4] === transcriptId))));
    case "coach-review": return Boolean(coachId && key[3] === coachId && (!leaderId || key[2] === leaderId));
    case "coach-transcript-full": return Boolean((transcriptId || coachId) && (!transcriptId || key[1] === transcriptId)
      && (!coachId || key[2] === coachId) && (!leaderId || key[3] === leaderId));
    case "coach-transcript-practice-summary":
    case "coach-transcript-leader": return Boolean(leaderId && key[1] === leaderId);
    case "coach-session-prep-v2": return Boolean(coachId && key[1] === coachId && (!leaderId || key[2] === leaderId));
    // Meeting ID is the only identity in this key. No supplied selector can
    // identify it; targeted saves do not invalidate other actors' meetings.
    default: return false;
  }
}

export async function invalidateTranscriptAnalysisQueries({
  queryClient,
  transcriptId,
  leaderId,
  coachId,
  proactiveMemoryReadback = "nonavailable",
  extraQueryKeys = [],
}: InvalidateTranscriptAnalysisOptions) {
  const rootKeys = [
    appQueryKeys.weeklyMomentsRoot(),
    appQueryKeys.todayScheduleRoot(),
    appQueryKeys.upcomingMeetingsRoot(),
    appQueryKeys.dashboardDataRoot(),
    appQueryKeys.transcriptUploadsThisWeekRoot(),
    appQueryKeys.livingConstellationStateRoot(),
    appQueryKeys.leaderInsightsRoot(),
    appQueryKeys.transcriptAnalysesRoot(),
    appQueryKeys.leaderTranscriptTrendsRoot(),
    appQueryKeys.postMeetingListRoot(),
    appQueryKeys.todayAnalyzedTranscriptCountRoot(),
    appQueryKeys.leaderExperienceRoot(),
    appQueryKeys.coachNewEvidenceRoot(),
    appQueryKeys.coachTranscriptAnalysesRoot(),
    appQueryKeys.coachReviewRoot(),
    appQueryKeys.coachReviewCountRoot(),
    appQueryKeys.coachTranscriptFullRoot(),
    appQueryKeys.coachTranscriptPracticeSummaryRoot(),
    appQueryKeys.coachTranscriptMeetingRoot(),
    appQueryKeys.coachTranscriptLeaderRoot(),
    appQueryKeys.coachSessionPrepV2Root(),
    appQueryKeys.coachSessionRecordsNeedsReviewRoot(),
    appQueryKeys.veeraSelectedMeetingContextRoot(),
    appQueryKeys.veeraChatContextRoot(),
  ];
  const knownScope = Boolean(coachId || leaderId || transcriptId);
  const invalidations = knownScope ? [queryClient.invalidateQueries({ predicate: query =>
    rootKeys.some(key => key[0] === query.queryKey[0]) && matchesTranscriptScope(query.queryKey, { coachId, leaderId, transcriptId }) })]
    : rootKeys.map(queryKey => queryClient.invalidateQueries({ queryKey }));
  invalidations.push(...extraQueryKeys.map(queryKey => queryClient.invalidateQueries({ queryKey })));


  if (transcriptId) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: appQueryKeys.transcriptAnalysisResult(transcriptId) }),
      queryClient.invalidateQueries({ queryKey: appQueryKeys.coachSessionRecord(transcriptId) }),
      queryClient.invalidateQueries({ queryKey: appQueryKeys.transcriptEvidenceDetail(transcriptId) }),
      queryClient.invalidateQueries({ queryKey: appQueryKeys.reflectionDataForAnalysis(transcriptId) }),
    );
  }

  if (leaderId) {
    if (proactiveMemoryReadback !== "none") {
      invalidations.push(
        refreshProactiveMemory(
          queryClient,
          appQueryKeys.proactiveMemoryLeader(leaderId),
          proactiveMemoryReadback,
        ),
      );
      if (coachId) {
        invalidations.push(
          refreshProactiveMemory(
            queryClient,
            appQueryKeys.proactiveMemoryCoach(coachId, leaderId),
            proactiveMemoryReadback,
          ),
        );
      }
    }
  }

  await Promise.all(invalidations);
}
